import { useState, useEffect, useMemo } from 'react';

import { KYC_DOCUMENTS_PER_PAGE } from '@/features/kyc/constants';

import { TComputedKycDocument } from '@/features/kyc/store/models/kyc.model';

import { useKycDocuments } from './useKycDocuments';

type TUseDocumentFilterProps = {
  documents: TComputedKycDocument[];
};

export function useKycDocumentFilter({ documents }: TUseDocumentFilterProps) {
  const [activePage, setActivePage] = useState<number>(0);

  const [filterStatus, setFilterStatus] = useState<string[] | undefined>();
  const [filterType, setFilterType] = useState<string[] | undefined>();

  const { documentStatusProps, documentTranslation } = useKycDocuments();

  const statusOptions = useMemo(() => {
    return [...new Set(documents.map((document) => document.kycStatus))].map((kycStatus) => ({
      label: documentStatusProps?.[kycStatus]?.label,
      value: kycStatus,
    }));
  }, [documentStatusProps, documents]);

  const typeOptions = useMemo(() => {
    return [...new Set(documents.map((document) => document.documentType))].map((documentType) => ({
      label: documentTranslation(documentType),
      value: documentType,
    }));
  }, [documentTranslation, documents]);

  const filteredData = useMemo(() => {
    let filteredDocuments = documents;
    if (filterStatus?.length > 0) {
      filteredDocuments = filteredDocuments.filter((document) =>
        filterStatus.includes(documentStatusProps?.[document?.kycStatus]?.label),
      );
    }

    if (filterType?.length > 0) {
      filteredDocuments = filteredDocuments.filter((document) =>
        filterType.includes(documentTranslation(document.documentType)),
      );
    }

    return filteredDocuments;
  }, [documents, filterStatus, filterType, documentTranslation, documentStatusProps]);

  useEffect(() => {
    if (filteredData.length <= KYC_DOCUMENTS_PER_PAGE && activePage > 0) {
      setActivePage(0);
    }
  }, [activePage, filteredData]);

  const documentCount = filteredData.length;
  const rows = filteredData.slice(activePage * KYC_DOCUMENTS_PER_PAGE, (activePage + 1) * KYC_DOCUMENTS_PER_PAGE);

  const onResetFilter = () => {
    setFilterStatus(undefined);
    setFilterType(undefined);
  };

  return {
    statusOptions,
    filterStatus,
    handleFilterStatus: (statuses: string[]) => setFilterStatus(statuses),
    typeOptions,
    filterType,
    handleFilterType: (types: string[]) => setFilterType(types),
    rows,
    documentCount,
    hasPagination: documentCount > KYC_DOCUMENTS_PER_PAGE,
    activePage,
    handlePageChange: (page: number) => setActivePage(page),
    hasFilter: Boolean(filterType || filterStatus),
    onResetFilter,
  };
}
